/**
 * Página de Gestão de Add-ons - Painel Admin
 * Permite gerenciar add-ons de tenants, sincronizar com Stripe
 * e realizar manutenções manuais quando necessário.
 * 
 * @version 1.0
 * @date 2025-12-19
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Package, 
  RefreshCw, 
  AlertTriangle, 
  History, 
  Plus,
  TrendingUp,
  TrendingDown,
  AlertCircle
} from 'lucide-react';
import { AddonListTab } from '@/components/admin/addon-management/AddonListTab';
import { AddonSyncTab } from '@/components/admin/addon-management/AddonSyncTab';
import { AddonDiagnosticsTab } from '@/components/admin/addon-management/AddonDiagnosticsTab';
import { AddonAuditTab } from '@/components/admin/addon-management/AddonAuditTab';
import { AddonCreateDialog } from '@/components/admin/addon-management/AddonCreateDialog';
import { useAddonManagement } from '@/hooks/useAddonManagement';

/**
 * Componente principal da página de Gestão de Add-ons
 */
export default function AddonManagement() {
  const { t } = useTranslation('admin');
  const [activeTab, setActiveTab] = useState('list');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  
  // Hook com dados e operações de add-ons
  const { 
    stats, 
    isLoadingStats,
    diagnostics,
    isLoadingDiagnostics,
    refetchAll
  } = useAddonManagement();

  return (
    <div className="space-y-6">
      {/* Header da página */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {t('addonManagement.title', 'Gestão de Add-ons')}
          </h1>
          <p className="text-muted-foreground">
            {t('addonManagement.description', 'Gerencie add-ons contratados pelos tenants')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => refetchAll()}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('common.refresh', 'Atualizar')}
          </Button>
          <Button 
            size="sm"
            onClick={() => setShowCreateDialog(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('addonManagement.actions.createManual', 'Cadastro Manual')}
          </Button>
        </div>
      </div>

      {/* Cards de KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total de Add-ons Ativos */}
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              {t('addonManagement.kpis.activeAddons', 'Add-ons Ativos')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-foreground">
                {isLoadingStats ? '...' : stats?.activeAddons ?? 0}
              </span>
              {stats?.addonsGrowth && stats.addonsGrowth > 0 && (
                <Badge variant="secondary" className="text-emerald-600">
                  <TrendingUp className="h-3 w-3 mr-1" />
                  +{stats.addonsGrowth}%
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Pendentes de Instância */}
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              {t('addonManagement.kpis.pendingInstances', 'Instâncias Pendentes')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-foreground">
                {isLoadingStats ? '...' : stats?.pendingInstances ?? 0}
              </span>
              {(stats?.pendingInstances ?? 0) > 0 && (
                <Badge variant="destructive">
                  {t('common.attention', 'Atenção')}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Problemas Detectados */}
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              {t('addonManagement.kpis.issues', 'Problemas Detectados')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-foreground">
                {isLoadingDiagnostics ? '...' : diagnostics?.total_issues ?? 0}
              </span>
              {(diagnostics?.total_issues ?? 0) > 0 && (
                <Badge variant="outline" className="text-amber-600 border-amber-300">
                  {t('addonManagement.kpis.needsReview', 'Revisar')}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Sem Stripe ID */}
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <TrendingDown className="h-4 w-4" />
              {t('addonManagement.kpis.withoutStripe', 'Sem Stripe ID')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-foreground">
                {isLoadingDiagnostics ? '...' : diagnostics?.addons_without_stripe ?? 0}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs de funcionalidades */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
          <TabsTrigger value="list" className="gap-2">
            <Package className="h-4 w-4" />
            <span className="hidden sm:inline">
              {t('addonManagement.tabs.list', 'Lista')}
            </span>
          </TabsTrigger>
          <TabsTrigger value="sync" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            <span className="hidden sm:inline">
              {t('addonManagement.tabs.sync', 'Sincronização')}
            </span>
          </TabsTrigger>
          <TabsTrigger value="diagnostics" className="gap-2">
            <AlertTriangle className="h-4 w-4" />
            <span className="hidden sm:inline">
              {t('addonManagement.tabs.diagnostics', 'Diagnóstico')}
            </span>
          </TabsTrigger>
          <TabsTrigger value="audit" className="gap-2">
            <History className="h-4 w-4" />
            <span className="hidden sm:inline">
              {t('addonManagement.tabs.audit', 'Histórico')}
            </span>
          </TabsTrigger>
        </TabsList>

        {/* Conteúdo das Tabs */}
        <TabsContent value="list" className="mt-4">
          <AddonListTab onCreateClick={() => setShowCreateDialog(true)} />
        </TabsContent>

        <TabsContent value="sync" className="mt-4">
          <AddonSyncTab />
        </TabsContent>

        <TabsContent value="diagnostics" className="mt-4">
          <AddonDiagnosticsTab 
            diagnostics={diagnostics} 
            isLoading={isLoadingDiagnostics}
            onCreateManual={() => setShowCreateDialog(true)}
            onRefreshData={refetchAll}
          />
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <AddonAuditTab />
        </TabsContent>
      </Tabs>

      {/* Dialog de Cadastro Manual */}
      <AddonCreateDialog 
        open={showCreateDialog} 
        onOpenChange={setShowCreateDialog}
        onSuccess={() => {
          setShowCreateDialog(false);
          refetchAll();
        }}
      />
    </div>
  );
}
